import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Shield } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { settingsSections } from "./nav-config";
import type { View } from "./nav-config";

export function SettingsNavItem({
  activeView,
  onSelect,
  canManageMembers,
  onSubItemSelect,
  mobile = false,
}: {
  activeView: View;
  onSelect: (view: View) => void;
  canManageMembers: boolean;
  // Callback opzionale chiamata dopo aver navigato a /settings#<hash>.
  // Utile nel mobile sheet per chiudere il pannello dopo la selezione.
  onSubItemSelect?: () => void;
  // Su mobile il click sul titolo fa solo toggle accordion, mai naviga
  // (lo sheet si chiuderebbe e l'accordion non sarebbe più visibile).
  mobile?: boolean;
}) {
  const { t } = useTranslation("shell");
  const location = useLocation();
  const navigate = useNavigate();
  const isOnSettings = activeView === "settings";
  const [expanded, setExpanded] = useState(isOnSettings || mobile);

  useEffect(() => {
    if (isOnSettings) setExpanded(true);
  }, [isOnSettings]);

  const sections = settingsSections.filter((s) => !s.adminOnly || canManageMembers);
  const activeHash = location.hash.replace(/^#/, "");

  return (
    <div>
      <button
        onClick={() => {
          if (mobile || isOnSettings) setExpanded((v) => !v);
          else onSelect("settings");
        }}
        className={cn(
          "relative w-full flex items-center gap-3 h-10 px-3 rounded-xl text-left transition-colors",
          isOnSettings
            ? "nav-rail-pill font-semibold"
            : "text-muted-foreground hover:bg-raised hover:text-foreground",
        )}
      >
        <Shield className="h-[18px] w-[18px]" />
        <span className="flex-1">{t("nav.settings")}</span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 transition-transform", expanded ? "" : "-rotate-90")}
        />
        {isOnSettings && <span className="nav-rail" aria-hidden />}
      </button>

      {expanded && (
        <div className="mt-px ml-[1.0625rem] pl-2.5 border-l border-border/60 flex flex-col gap-px">
          {sections.map((section) => {
            const isActive = isOnSettings && activeHash === section.hash;
            return (
              <button
                key={section.id}
                onClick={() => {
                  navigate(`/settings#${section.hash}`);
                  onSubItemSelect?.();
                }}
                className={cn(
                  "px-2.5 py-1 rounded-md text-[12.5px] text-left transition-colors",
                  isActive
                    ? "bg-muted/70 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-raised hover:text-foreground",
                )}
              >
                {t(section.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
